import { useQueryClient } from 'react-query';
import { useOfflineMutation } from '../../../common/offline';
import { requestGQL } from '../../../common/graphQlClient';
import { UPDATE_OUTLET_LOCATION } from '../api';
import type { GetPropertyConnectionResponse, NodeData } from '../../connection/api';
import type { Lambda } from '../../../common/types';

type SuccessErrorCallbacks = {
  onSuccess: Lambda<void, void>;
  onError: Lambda<void, void>;
};

export const usePatchOutlet = (propertyId: number, { onSuccess, onError }: SuccessErrorCallbacks) => {
  const queryClient = useQueryClient();

  const applyOptimisticUpdate: (v: any) => void = (optimisticData: {
    outletId: string;
    location?: string;
    contactType?: string;
    propertyId: number;
  }) => {
    queryClient.setQueryData<GetPropertyConnectionResponse | undefined>(['propertyConnection', propertyId], (old) => {
      if (!old?.propertyById?.connectionByConnectionId?.serialized2) return old;
      const updateOutletNodeIdx = old.propertyById.connectionByConnectionId.serialized2.nodeDataArray.findIndex(
        ({ id }) => optimisticData.outletId === id,
      );
      if (updateOutletNodeIdx < 0) return old;
      const oldOutletData = old.propertyById.connectionByConnectionId.serialized2.nodeDataArray[updateOutletNodeIdx];
      const oldCloned = structuredClone(old);
      (oldCloned.propertyById.connectionByConnectionId.serialized2.nodeDataArray as Array<NodeData>)[
        updateOutletNodeIdx
      ] = {
        ...oldOutletData,
        cfObject: {
          ...(oldOutletData.cfObject ?? {}),
          location: optimisticData.location ?? oldOutletData.cfObject?.location,
          contactType: optimisticData.contactType ?? oldOutletData.cfObject?.contactType,
        },
      };

      return oldCloned;
    });
  };

  const offlineMutation = useOfflineMutation({
    mutationFn: (payload: { outletId: string; propertyId: number; location: string; contactType?: string }) =>
      requestGQL(UPDATE_OUTLET_LOCATION, {
        propertyId: payload.propertyId,
        outletId: payload.outletId,
        location: payload.location,
        contactType: payload.contactType ?? null,
      }),
    mutationName: 'updateOutletLocation',
    entity: 'outlet',
    operationType: 'UPDATE',
    propertyId,
    invalidateKeys: [['propertyConnection', propertyId]],
    graphqlDocument: UPDATE_OUTLET_LOCATION,
    getOptimisticData: (v) => v,
    applyOptimisticUpdate,
    onSuccess: () => onSuccess(),
    onError: () => onError(),
    onOfflineQueued: () => onSuccess(),
  });

  return {
    ...offlineMutation,
    mutate: (input: { id: string; location?: string; contactType?: string }) =>
      offlineMutation.mutate({
        propertyId,
        outletId: input.id,
        location: input.location ?? '',
        contactType: input.contactType,
      }),
    mutateAsync: (input: { id: string; location?: string; contactType?: string }) =>
      offlineMutation.mutateAsync({
        propertyId,
        outletId: input.id,
        location: input.location ?? '',
        contactType: input.contactType,
      }),
  };
};
